import { ReactNode, useEffect, useRef } from "react";

import { Collapse, FormHelperText, Stack } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";

import pxToRem from "theme/base/pxToRem";

import PreviewFile from "./PreviewFile";
import UploadMore from "./UploadMore";

import { get } from "lodash";

import { FileNumber } from "./style";
import useMultiFilePicker from "helper/customHook/useFileManager";
import { postFileRes } from "core/http/service/personnelInformation/resTypes";

interface Props {
  name: string;
  formData: object;
  formErrors?: object;
  buttonProps?: ButtonProps;
  attachRef?: Function;
  formUploadProps?: {
    accept?: string | string[];
    multiple?: boolean;
    readAs?: "DataURL" | "Text" | "BinaryString" | "ArrayBuffer";
  };
  title?: string | ReactNode;
  onChange?: (
    name: string
  ) => (
    plainFiles: File[] | (string | ArrayBuffer | null | postFileRes)[]
  ) => void;
  previewStyle?: object;
}

const FormUpload = ({
  name,
  formData,
  buttonProps,
  formErrors,
  title = "Upload",
  formUploadProps,
  attachRef,
  onChange,
  previewStyle,
}: Props) => {
  const prevDefaultValueRef = useRef<Array<postFileRes> | null>(null);
  const { openFilePicker, filesContent, remove, clear } = useMultiFilePicker({
    onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
      // Đối với các file mới được cần được upload file dưới dạng formData
      // Dữ liệu này sẽ được lưu với biến mới dưới dạng: name + hậu tố files
      onChange?.(`${name}_files`)(plainFiles);
      onChange?.(`${name}_file_contents`)(filesContent);
    },
    defaultValue: {
      plainFiles: get(formData, `${name}_files`) ?? [],
      filesContent: get(formData, `${name}_file_contents`) ?? [],
    },
    ...formUploadProps,
  });

  const defaultValue: Array<postFileRes> = get(formData, name) ?? [];
  const plainFiles = get(formData, `${name}_files`) ?? [];

  useEffect(() => {
    // Access the previous value
    const prevDefaultValue = prevDefaultValueRef.current;
    if (prevDefaultValue && defaultValue.length - prevDefaultValue.length > 0) {
      clear();
    }

    // Update the ref with the current value
    prevDefaultValueRef.current = defaultValue;
  }, [defaultValue]);

  const removeDefault = (index: number) => {
    // các file được gửi từ server hiển thị dưới dạng url
    // hàm này dùng để xóa các url trong mảng data từ server
    const newDefaultValue = defaultValue.filter((_, i) => i !== index);
    onChange?.(name)(newDefaultValue);
  };

  return (
    <>
      <>
        {filesContent.length > 0 || defaultValue.length > 0 ? (
          <Stack gap={pxToRem(10)}>
            <FileNumber>{`${filesContent.length + defaultValue.length} File đính kèm`}</FileNumber>
            <Stack direction={"row"} gap={pxToRem(16)}>
              {/* defaultValue là 1 mảng các url */}
              {defaultValue.map((url, index) => {
                if (url?.originalFilename) {
                  return (
                    <PreviewFile
                      // Tách từ url, lấy thông tin name file và content để preview
                      file={{
                        name: url.originalFilename || "",
                        content: url.path,
                      }}
                      key={index}
                      remove={() => removeDefault(index)}
                    />
                  );
                } else {
                  return (
                    <PreviewFile
                      file={url}
                      key={index}
                      remove={() => removeDefault(index)}
                      error
                    />
                  );
                }
              })}
              {filesContent.filter(Boolean).map((file, index) => (
                <PreviewFile
                  file={file}
                  key={index}
                  remove={() => {
                    onChange?.(`${name}_files`)(
                      plainFiles.filter((_, i) => i !== index)
                    );
                    onChange?.(`${name}_file_contents`)(
                      filesContent.filter((_, i) => i !== index)
                    );
                    remove(index);
                  }}
                  style={previewStyle}
                />
              ))}
              {formUploadProps?.multiple && (
                <UploadMore onClick={openFilePicker} />
              )}
            </Stack>
          </Stack>
        ) : (
          <Button {...buttonProps} onClick={openFilePicker}>
            {title}
          </Button>
        )}
        <Collapse
          in={!!formErrors?.[name] || !!formErrors?.[`${name}_files`]}
          timeout="auto"
        >
          <FormHelperText error>
            {formErrors?.[name] ?? formErrors?.[`${name}_files`]}
          </FormHelperText>
        </Collapse>
      </>
      <div ref={attachRef?.(`${name}_files`)}></div>
      <div ref={attachRef?.(`${name}`)}></div>
    </>
  );
};

export default FormUpload;
